import { Dispatch, SetStateAction, useContext, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  tokens,
  Typography,
  UtilityConnectionStatusPill,
} from "@sunrun/experience-ui-components";

import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import {
  FileUploadError,
  UploadAttachments,
} from "components/molecules/UploadAttachments";
import { UploadErrorModal } from "components/molecules/UploadErrorModal";
import { UtilityBillUploadCard } from "components/organisms/UtilityBillUploadCard";
import { Attachment } from "utils/usageUtils";
import { UsageFormContext } from "providers/UsageFormContext";
import {
  ImmediateUploadConfig,
  useImmediateAttachmentUpload,
} from "hooks/useImmediateAttachmentUpload";

type DocumentUploadProps = {
  uploadedFiles: Attachment[] | SplatCheckoutDocument[];
  uploadedNewFiles?: Attachment[];
  setUploadedNewFiles?: Dispatch<SetStateAction<Attachment[]>>;
  immediateUpload?: ImmediateUploadConfig;
  documentType: string;
  description: string;
  title: string;
  documentLoading?: boolean;
  getBillOnline?: () => void;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
  bannerContent?: React.ReactNode;
  onDeleteUploadedFile?: (
    file: Attachment & { documentId: string }
  ) => Promise<void> | void;
  markUploadDirty?: boolean;
  isDeleteBlocked?: boolean;
};

const UTILITY_BILL_DOCUMENT_TYPE = "Utility Bill";

const DocumentUpload = ({
  uploadedFiles,
  uploadedNewFiles = [],
  setUploadedNewFiles,
  immediateUpload,
  documentType,
  description,
  title,
  documentLoading,
  getBillOnline,
  intervalDataInitialLoading,
  intervalDataStatus,
  bannerContent,
  onDeleteUploadedFile,
  markUploadDirty = true,
  isDeleteBlocked = false,
}: DocumentUploadProps) => {
  const [errors, setErrors] = useState<FileUploadError[]>([]);
  const { setIsDirty } = useContext(UsageFormContext);
  const setUploadDirty = markUploadDirty ? setIsDirty : () => {};
  const {
    pendingAttachments: immediatePendingAttachments,
    setPendingAttachments: setImmediatePendingAttachments,
    isUploading,
  } = useImmediateAttachmentUpload(immediateUpload);

  const pendingAttachments = immediateUpload
    ? immediatePendingAttachments
    : uploadedNewFiles;
  const setPendingAttachments = immediateUpload
    ? setImmediatePendingAttachments
    : setUploadedNewFiles!;
  const isLoading =
    documentLoading || isUploading || intervalDataInitialLoading;

  if (documentType === UTILITY_BILL_DOCUMENT_TYPE) {
    return (
      <div data-testid="document-upload-utility-bill">
        {bannerContent}
        <UtilityBillUploadCard
          title={title}
          uploadedFiles={uploadedFiles}
          uploadedNewFiles={pendingAttachments}
          setUploadedNewFiles={setPendingAttachments}
          onDeleteUploadedFile={onDeleteUploadedFile}
          getBillOnline={getBillOnline}
          utilityConnectionStatus={intervalDataStatus || ""}
          documentLoading={isLoading}
          setIsDirty={setUploadDirty}
          isDeleteBlocked={isDeleteBlocked}
        />
      </div>
    );
  }

  const legacyLoading = documentLoading || intervalDataInitialLoading;

  const uploadCard = (
    <Card>
      <HeaderContainer data-testid="document-upload-header">
        <Typography size={tokens.FONT_SIZE_3}>{title}</Typography>
        <UtilityConnectionStatusPill status={intervalDataStatus || ""} />
      </HeaderContainer>
      <CardHeaderDivider />
      {bannerContent}
      <div className="document-upload-body">
        <UploadAttachments
          buttonText="Manual bill upload"
          buttonColor="secondary"
          setIsDirty={setIsDirty}
          uploadedFiles={uploadedFiles}
          documentType={documentType}
          buttonSize="md"
          uploadedNewFiles={pendingAttachments}
          onUploadedNewFiles={setPendingAttachments}
          onError={(fileErrors) => setErrors(fileErrors)}
          description={description}
          allowDownload
          allowPreview
          documentLoading={legacyLoading}
          getBillOnline={getBillOnline}
          intervalDataStatus={intervalDataStatus || ""}
        />
      </div>
      <UploadErrorModal errors={errors} onClose={() => setErrors([])} />
    </Card>
  );

  return uploadCard;
};

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export { DocumentUpload };
export type { DocumentUploadProps };
